import { Box, Button, TextField } from "@mui/material";
import React, { useEffect } from 'react';
import { campaignManagementService } from "services/campaign";
import { DEFAULT_TOKEN } from "utilities/constants";

interface AIConversationProps {
  expertId: string;
  jobId: string;
  campaignId: string;
  username: string;
}

interface ConversationMessage {
  from: string;
  message: string;
  createdAt: number;
  generatedBy: string;
  autoResponseUpdatedAt?: number;
}

export const AIConversation: React.FC<AIConversationProps> = ({
  expertId,
  jobId,
  campaignId,
  username,
}) => {
    const [conversation, setConversation] = React.useState<ConversationMessage[]>([]);
    const [newMessage, setNewMessage] = React.useState("");
    const [conversationId, setConversationId] = React.useState("");
    const [autoResponse, setAutoResponse] = React.useState(false);

    const messagesContainerRef = React.useRef<HTMLDivElement | null>(null);

    const scrollToBottom = () => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    };

    const fetchConversation = async () => {
        const res = await campaignManagementService.getConversation({
            token: DEFAULT_TOKEN,
            expertId,
            jobId,
            campaignId,
            username,
        });

        const conversationData = res.output.conversation;
        setConversation(conversationData.conversations.linkedinConversationEvents || []);
        setConversationId(conversationData.conversationId);
        setAutoResponse(conversationData.autoResponse);
        setTimeout(scrollToBottom, 0);
    };

    const sendMessage = async () => {
        await campaignManagementService.postConversation({
            token: DEFAULT_TOKEN,
            expertId,
            jobId,
            campaignId,
            username,
            conversationId,
            message: newMessage,
        });
        setNewMessage("");
        fetchConversation();
    };

    useEffect(() => {
        fetchConversation();
    }, []);

    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {conversation.length > 0 && (
          <Box sx={{ px: 2, pt: 2 }}>
            <Button
              variant="outlined"
              color={autoResponse ? 'success' : 'inherit'}
              size="small"
              sx={{ borderRadius: '16px' }}
              onClick={async () => {
                const updatedAutoResponse = !autoResponse;
                setAutoResponse(updatedAutoResponse);
                const res = await campaignManagementService.updateAutoResponse({
                  token: DEFAULT_TOKEN,
                  expertId,
                  conversationId,
                  jobId,
                  campaignId,
                  username,
                  autoResponse: updatedAutoResponse
                });
                if (res?.output?.autoResponse !== undefined) {
                  setAutoResponse(res.output.autoResponse);
                }
              }}
            >
              {autoResponse ? 'Auto Response: ON' : 'Auto Response: OFF'}
            </Button>
          </Box>
        )}
        <Box ref={messagesContainerRef} sx={{ flex: 1, overflowY: 'auto', p: 2, paddingBottom: '90px', minHeight: 0 }}>
          {[...conversation || {}]?.reverse().map((msg, i) => {
            const isCandidate = msg.generatedBy === 'CANDIDATE';
            return (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  justifyContent: isCandidate ? 'flex-end' : 'flex-start',
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    maxWidth: '70%',
                    p: 1.5,
                    borderRadius: '12px',
                    backgroundColor: isCandidate ? '#E0F2FE' : '#F3F4F6',
                    color: '#111827',
                  }}
                >
                  <Box sx={{ fontSize: '14px' }}>{msg.message}</Box>
                  <Box
                    sx={{
                      fontSize: '10px',
                      color: '#6B7280',
                      mt: 0.5,
                      textAlign: isCandidate ? 'right' : 'left',
                    }}
                  >
                    {new Date(msg.createdAt).toLocaleString()}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
        {conversation.length > 0 && (
          <Box
            sx={{
              borderTop: '1px solid #E5E7EB',
              display: 'flex',
              gap: 1,
              p: 2,
              alignItems: 'center',
              position: 'sticky',
              bottom: 0,
              backgroundColor: '#fff',
              zIndex: 1,
            }}
          >
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Write your response here..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
            <Button
              variant="contained"
              onClick={sendMessage}
              sx={{ borderRadius: '8px', minWidth: '48px' }}
            >
              Send
            </Button>
          </Box>
        )}
      </Box>
    );
};